import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Button,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import DialogActions from "@mui/material/DialogActions";
import * as XLSX from "xlsx";
import AlertBar from "./AlertBar";
import secureLocalStorage from "react-secure-storage";
import CircularProgress from "@mui/material/CircularProgress";
import { API_URL } from "../../config/environment";
import { useTranslation } from "react-i18next";

const ExportIngredients = ({ open, onClose }) => {
  const token = sessionStorage.getItem("authToken");
  const { t } = useTranslation();
  const [technofunctionality, setTechnofunctionality] = useState(null);
  const [categories, setCategories] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState(false);
  const [isloading, setIsLoading] = useState(false);

  const handleChange = (event) => {
    setTechnofunctionality(event.target.value);
  };

  function handleOnClose() {
    setTechnofunctionality(null);
    onClose();
  }

  function stringToArrayBuffer(s) {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
    return buf;
  }

  const createExcelSheet = async () => {
    setIsLoading(true);
    if (!technofunctionality) {
      setErrors(true);
      setErrorMessage("No Technofunctionality selected");
      setIsLoading(false);
      return;
    }

    try {
      // Get exported ingredients

      const ingredients = await exportIngredients(technofunctionality);
      if (!ingredients) {
        setErrors(true);
        setErrorMessage("Ingredients were not exported. Please try again");
        setIsLoading(false);
        return;
      }
      // Prepare headers
      const headers = Array.from(
        new Set(ingredients.flatMap((ingredient) => Object.keys(ingredient)))
      ); // Combine all unique headers from all ingredients
      console.log(headers);

      // Convert the array of objects into an array of arrays
      let data = [headers]; // Start with headers
      ingredients.forEach((obj) => {
        let rowData = [];
        headers.forEach((header) => {
          let value = obj[header];
            if (typeof value === "string") {
              // 1. Clean up the string (removes carriage returns and extra whitespace)
              value = value.replace(/\r/g, "").trim();

              // 2. Safeguard the Excel limit
              if (value.length > 32760) {
                console.warn(`Row ${obj._id} - ${header} still too long after cleaning. Truncating.`);
                value = value.substring(0, 32730) + "... [TRUNCATED]";
              }
            }

          rowData.push(value);
        });
        data.push(rowData);
      });

      // Create a new workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

      // Generate Excel file as a Blob
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const currentDate = new Date().toISOString()?.split("T")[0];
      const currentTime = new Date()
        .toISOString()
        ?.split("T")[1]
        ?.split(".")[0];
      const filename = `${technofunctionality}_${currentDate}_${currentTime}`;

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.xlsx`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 0);
    } catch (error) {
      setErrors(true);
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  async function getCategories() {
    const url = `${API_URL}/categories?categoryType=technofunctionality`;

    const response = await fetch(url);
    const data = await response.json();

    if (data.status === "success") {
      const categories = data.categories;
      return categories;
    } else {
      console.log("Could not fetch data from server");
      setErrors(true);
      setErrorMessage(data.message);
    }
  }

  async function exportIngredients(technofuctionality) {
    const url = `${API_URL}/ingredients/export?technofunctionality=${technofuctionality}`;

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    console.log({ data });

    if (data.status === "success") {
      const ingredients = data.ingredients;
      return ingredients;
    } else {
      console.log("Could not fetch data from server");
      setErrors(true);
      setErrorMessage(data.message);
    }
  }

  useEffect(() => {
    (async () => {
      const categories = await getCategories();
      if (categories) {
        const defaultOption = t("dashboard.create-sheet.default-option");
        const _categories = JSON.stringify(categories);
        secureLocalStorage.setItem("categories", _categories);
        categories.unshift(defaultOption);
        setCategories([...categories]);
      }
    })();

    // TODO: Implement a cleanup to abort requests using AbortController
    //  return () => {
    //    active = false;
    //  };
  }, [t]);

  const dialogStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(5px)",
  };

  const closeButtonStyle = {
    position: "absolute",
    top: 0,
    right: 0,
    margin: "16px", // Add margin as needed
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        style={dialogStyle}
        maxWidth="xl"
        fullWidth={true}
      >
        {/* Close button */}
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleOnClose}
          style={closeButtonStyle}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle>
          <Typography variant="h5" gutterBottom fontWeight={700}>
            Export Ingredients
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Display current step */}

          <Typography variant="h6" gutterBottom fontWeight={300} fontSize={14}>
            This creates an Excel sheet template for the selected
            technofunctionality
          </Typography>

          <FormControl sx={{ m: 1, minWidth: 500 }}>
            <InputLabel id="export-data-label">Technofunctionality</InputLabel>
            <Select
              labelId="export-data-lable"
              id="export-data-lable"
              value={technofunctionality}
              required
              label="Technofunctionality"
              placeholder="Select your technfunctionality"
              onChange={handleChange}
              name="upload-sheet"
              error={errors}
              helperText={errors && "Technofunctionality is required"}
            >
              {categories &&
                categories.map((category) => (
                  <MenuItem value={category}>{category}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            //   endIcon={<AddIcon />}
            color="success"
            //   component={Link}
            onClick={async () => {
              await createExcelSheet();
            }}
            disabled={technofunctionality ? false : true}
            sx={{ margin: "20px" }}
          >
            Export
            {isloading && <CircularProgress size={20} color="inherit" />}
          </Button>
        </DialogActions>
      </Dialog>
      <AlertBar
        open={errors}
        message={errorMessage}
        severity={"error"}
        onClose={() => setErrors(false)}
      />
    </>
  );
};

export default ExportIngredients;
